import { useCallback, useEffect, useMemo, useState, useRef } from "react";

import {
  BarChart3,
  CheckCircle,
  Clock,
  ClipboardCheck,
  List,
  LineChart,
  Plus,
  Timer,
} from "lucide-react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import ApprovalPendingPanel from "./ApprovalPendingPanel";
import CycleHistoryPanel from "./CycleHistoryPanel";
import type { CycleHistoryRow } from "./CycleHistoryPanel";
import DiscoveryHubConfirmModal from "./DiscoveryHubConfirmModal";
import DiscoveryHubPollSectionAccordion from "./DiscoveryHubPollSectionAccordion";
import {
  SampleCompletedPollCards,
  SampleDataOverlay,
  SampleLatePollCards,
  SampleOngoingPollCards,
} from "./DiscoveryHubSampleState";
import DraftLeaderboardPanel, {
  type LeaderboardPayload,
} from "./DraftLeaderboardPanel";
import ExecutiveSummaryPanel, {
  type ExecutiveSummaryPayload,
} from "./ExecutiveSummaryPanel";
import LaunchPollModal, { type LaunchPollFormState } from "./LaunchPollModal";
import { isConfiguredLeaderboardApprover } from "./leaderboardApproverUtils";
import PollAccordionContent from "./PollAccordionContent";
import {
  buildTabSponsorBreakdown,
  countSponsorsForHubTab,
  groupMatchesHubTab,
  groupPollGroupsByPeriod,
  sortPollGroupsByLaunchDate,
  sponsorPoll1IsComplete,
  teamFeedbackStatusLabel,
  teamMemberHasPendingResponse,
  type TeamPoll2Row,
} from "./pollAccordionHelpers";
import PollCycleCard from "./PollCycleCard";
import Button from "../../Components/Button";
import DatePicker from "../../Components/DatePicker";
import EngagementEmptyPanel, {
  HubPageLoading,
} from "../../Components/EngagementEmptyPanel";
import Modal from "../../Components/Modal";
import TabsComponent from "../../Components/Tab";
import Table from "../../Components/Table";
import { useAppDispatch } from "../../Store/Hooks";
import {
  fetchApprovalPendingLeaderboard,
  fetchCycles,
  fetchDiscoveryHub,
  fetchEngagementConfig,
  fetchExecutiveSummary,
  fetchLaunchContext,
  fetchLeaderboard,
  extendPollDueDate,
  launchPoll,
  saveDraftLeaderboard,
  type SaveDraftLeaderboardResult,
  sendBulkReminders as sendBulkRemindersThunk,
  sendPoll2Reminder as sendPoll2ReminderThunk,
  sendSponsorReminder,
} from "../../Store/Reducers/SponsorEngagementSlice";
import { normalizeRoleName } from "../../Utils/Constants/RoleConstants";
import {
  STATUS_CHIP_ROW,
  canAccessSponsorDrilldown,
  pollStatusChipClass,
  statusChipLabelClass,
  statusChipValueClass,
  statusChipValueUnitClass,
} from "../../Utils/Helper/discoveryHubUi";
import {
  STORAGE_KEYS,
  getFromLocalStorage,
} from "../../Utils/Helper/LocalStorage";
import {
  composePollNameFromSuffix,
  defaultLaunchFormState,
  firstEnabledAudience,
  hasParticipantLaunchBlocks,
  poll1FrequencyFromConfig,
  poll2FrequencyFromConfig,
  type DiscoveryHubLaunchDefaults,
  type LaunchContextPayload,
  isPastDueDate,
  isPollNameOnlyDefaultQuarter,
  periodForLaunch,
  quarterDateRangeTooltip,
} from "../../Utils/Helper/sponsorEngagementLaunch";
import { formatQuarterPeriodLabel } from "../../Utils/Helper/sponsorEngagementUi";
import { toast } from "../../Utils/Helper/toast";

const POLL_PROGRESS_ACTIONS = "flex gap-1.5 flex-wrap items-center";
const POLL_PROGRESS_TABLE =
  "bg-white rounded-lg border border-gray-200 overflow-hidden [&_.table-container]:w-full [&_table]:w-full [&_th]:align-middle [&_td]:align-middle [&_td:last-child]:whitespace-nowrap";
const OVERDUE_TABLE =
  "w-full border-collapse table-fixed text-sm [&_th]:bg-gray-50 [&_th]:px-4 [&_th]:py-3 [&_th]:text-xs [&_th]:font-medium [&_th]:text-gray-500 [&_th]:uppercase [&_th]:border-b [&_th]:border-gray-200 [&_th]:align-middle [&_td]:px-4 [&_td]:py-3 [&_td]:border-b [&_td]:border-gray-200 [&_td]:align-middle [&_tr:last-child_td]:border-b-0 [&_tr:hover]:bg-gray-50";
const OVERDUE_NAME_CELL =
  "font-semibold text-gray-800 truncate max-w-0 text-left";
const OVERDUE_METRIC_CELL = "text-center";
const OVERDUE_ACTION_CELL = "text-center";

const reminderBadgeClass = (badge: "zero" | "low" | "high") => {
  const base = "inline-flex px-2 py-0.5 rounded-full text-xs font-medium";
  if (badge === "zero") return `${base} bg-gray-100 text-gray-600`;
  if (badge === "low") return `${base} bg-amber-100 text-amber-800`;
  return `${base} bg-red-100 text-red-800`;
};

const ESL_HUB_TAB_IDS = [
  "ongoing",
  "late-window",
  "completed",
  "draft-leaderboard",
  "cycles",
] as const;

const APPROVER_HUB_TAB_IDS = [
  "ongoing",
  "late-window",
  "completed",
  "draft-leaderboard",
  "approval-pending",
  "executive-summary",
  "cycles",
] as const;

export type DiscoveryHubTab =
  | (typeof ESL_HUB_TAB_IDS)[number]
  | (typeof APPROVER_HUB_TAB_IDS)[number];

const hubTabIdsForAccess = (
  hasApproverHubAccess: boolean
): readonly DiscoveryHubTab[] =>
  hasApproverHubAccess ? APPROVER_HUB_TAB_IDS : ESL_HUB_TAB_IDS;

const isAnyDiscoveryHubTab = (v: string | null): v is DiscoveryHubTab =>
  v != null &&
  ((ESL_HUB_TAB_IDS as readonly string[]).includes(v) ||
    (APPROVER_HUB_TAB_IDS as readonly string[]).includes(v));

const isDiscoveryHubTab = (
  v: string | null,
  hasApproverHubAccess: boolean
): v is DiscoveryHubTab =>
  v != null &&
  (hubTabIdsForAccess(hasApproverHubAccess) as readonly string[]).includes(v);

/** True when the user was selected as approver on a pending leaderboard submission. */
const isAssignedLeaderboardApproverForUser = (
  leaderboard: LeaderboardPayload | null | undefined,
  userId: string
): boolean =>
  Boolean(
    userId &&
      leaderboard?.publication_state === "pending_approval" &&
      leaderboard?.assigned_approver_id === userId
  );

const fmtRelative = (isoDate: string | null | undefined) => {
  if (!isoDate) return "Never";
  const then = new Date(isoDate);
  const now = new Date();
  const days = Math.floor((now.getTime() - then.getTime()) / 86400000);
  if (days <= 0) return "Today";
  if (days === 1) return "1 day ago";
  if (days < 7) return `${days} days ago`;
  if (days < 14) return "Last week";
  return then.toLocaleDateString();
};

const defaultHubForPhase = (phase: string): DiscoveryHubTab => {
  if (phase === "late_window") return "late-window";
  if (phase === "review" || phase === "publish") return "draft-leaderboard";
  return "ongoing";
};

/** Sponsor still has at least one Poll 1 assignment without a response. */
const sponsorHasPendingResponse = (row: {
  outstanding_accounts?: number;
  overdue_accounts?: number;
  completed_accounts?: number;
  total_accounts?: number;
}) => {
  if ((row.outstanding_accounts ?? 0) > 0) return true;
  if ((row.overdue_accounts ?? 0) > 0) return true;
  const total = row.total_accounts ?? 0;
  const done = row.completed_accounts ?? 0;
  return total > 0 && done < total;
};

type PendingConfirmAction =
  | {
      type: "sponsor_reminder";
      sponsorId: string;
      cycleId?: string;
      label: string;
    }
  | {
      type: "poll2_reminder";
      teamMemberId: string;
      sponsorId: string;
      cycleId: string;
      label: string;
    }
  | {
      type: "bulk_reminder";
      sponsorRows: any[];
      teamRows: TeamPoll2Row[];
      label: string;
    }
  | { type: "save_draft"; label: string };

const DiscoveryHub = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const withOrg = useCallback(
    (path: string) => {
      const normalizedPath = path.startsWith("/") ? path : `/${path}`;
      return orgSlug ? `/${orgSlug}${normalizedPath}` : normalizedPath;
    },
    [orgSlug]
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const loggedIn = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const userRole = normalizeRoleName(loggedIn?.role);
  const canViewDrilldown = canAccessSponsorDrilldown(
    userRole,
    loggedIn?.executiveOps
  );
  const currentUserId = loggedIn?.userId ? String(loggedIn.userId) : "";
  const executiveOpsApprove = loggedIn?.executiveOpsApprove === true;
  const isConfiguredApprover = useMemo(
    () =>
      isConfiguredLeaderboardApprover({
        role: userRole,
        executiveOpsApprove,
      }),
    [executiveOpsApprove, userRole]
  );
  const hubFromUrl = searchParams.get("hub");
  const [hubData, setHubData] = useState<any>(null);
  const [hubConfig, setHubConfig] = useState<{
    cycle_config?: { cadence?: string };
    launch_defaults?: {
      reminder_cadence?: string;
      target_audience?: string;
      poll2_target_audience?: string;
    };
  } | null>(null);
  const [cycles, setCycles] = useState<CycleHistoryRow[]>([]);
  const [draftLeaderboard, setDraftLeaderboard] =
    useState<LeaderboardPayload | null>(null);
  const [approvalPendingLeaderboard, setApprovalPendingLeaderboard] =
    useState<LeaderboardPayload | null>(null);
  const [executiveSummary, setExecutiveSummary] =
    useState<ExecutiveSummaryPayload | null>(null);
  const [executiveSummaryLoading, setExecutiveSummaryLoading] = useState(false);
  const [leaderboardPeriod, setLeaderboardPeriod] = useState("");
  const [approvalPendingPeriod, setApprovalPendingPeriod] = useState("");
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [approvalPendingLoading, setApprovalPendingLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [launchOpen, setLaunchOpen] = useState(false);
  const [launchModalKey, setLaunchModalKey] = useState(0);
  const [launchSubmitting, setLaunchSubmitting] = useState(false);
  const [launchForm, setLaunchForm] = useState<LaunchPollFormState>(() =>
    defaultLaunchFormState()
  );
  const [expandedPollIds, setExpandedPollIds] = useState<
    Record<string, boolean>
  >({});
  const [selectedCompletedCycleIds, setSelectedCompletedCycleIds] = useState<
    Set<string>
  >(new Set());
  const [savingDraftLeaderboard, setSavingDraftLeaderboard] = useState(false);
  const [reminderLoadingId, setReminderLoadingId] = useState<string | null>(
    null
  );
  const [bulkReminderLoading, setBulkReminderLoading] = useState(false);
  const [hubRefreshing, setHubRefreshing] = useState(false);
  const [launchContext, setLaunchContext] =
    useState<LaunchContextPayload | null>(null);
  const [pendingConfirm, setPendingConfirm] =
    useState<PendingConfirmAction | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [launchModalOpening, setLaunchModalOpening] = useState(false);
  const [extendDueDateModal, setExtendDueDateModal] = useState<{
    isOpen: boolean;
    cycleId: string;
    pollName: string;
    currentDueDate: string;
    newDueDate: string;
    reason: string;
  }>({
    isOpen: false,
    cycleId: "",
    pollName: "",
    currentDueDate: "",
    newDueDate: "",
    reason: "",
  });
  const [extendingDueDate, setExtendingDueDate] = useState(false);

  const bulkReminderBusy = bulkReminderLoading || hubRefreshing;

  const phase = hubData?.phase || "collection";
  const initialHub = isAnyDiscoveryHubTab(hubFromUrl)
    ? hubFromUrl
    : defaultHubForPhase(phase);
  const [activeHub, setActiveHub] = useState<DiscoveryHubTab>(initialHub);

  const loadLeaderboard = useCallback(
    async (period: string) => {
      if (!period) {
        setDraftLeaderboard(null);
        return;
      }
      setLeaderboardLoading(true);
      const result = await dispatch(fetchLeaderboard({ period }));
      if (fetchLeaderboard.fulfilled.match(result)) {
        setDraftLeaderboard(result.payload as LeaderboardPayload);
        setLeaderboardPeriod(period);
      } else {
        toast.error((result.payload as string) || "Failed to load leaderboard");
        setDraftLeaderboard({ period, publication_state: null, rows: [] });
        setLeaderboardPeriod(period);
      }
      setLeaderboardLoading(false);
    },
    [dispatch]
  );

  const loadApprovalPendingLeaderboard = useCallback(
    async (period: string) => {
      if (!period) {
        setApprovalPendingLeaderboard(null);
        return;
      }
      setApprovalPendingLoading(true);
      const result = await dispatch(fetchApprovalPendingLeaderboard(period));
      if (fetchApprovalPendingLeaderboard.fulfilled.match(result)) {
        setApprovalPendingLeaderboard(result.payload as LeaderboardPayload);
        setApprovalPendingPeriod(period);
      } else {
        toast.error(
          (result.payload as string) || "Failed to load approval queue"
        );
        setApprovalPendingLeaderboard({
          period,
          publication_state: null,
          rows: [],
        });
        setApprovalPendingPeriod(period);
      }
      setApprovalPendingLoading(false);
    },
    [dispatch]
  );

  const loadExecutiveSummary = useCallback(async () => {
    setExecutiveSummaryLoading(true);
    const result = await dispatch(fetchExecutiveSummary());
    if (fetchExecutiveSummary.fulfilled.match(result)) {
      setExecutiveSummary(result.payload as ExecutiveSummaryPayload);
    } else {
      toast.error(
        (result.payload as string) || "Failed to load executive summary"
      );
      setExecutiveSummary(null);
    }
    setExecutiveSummaryLoading(false);
  }, [dispatch]);

  const resolveLeaderboardPeriod = useCallback(() => {
    const fallback = periodForLaunch("quarterly");
    return (
      leaderboardPeriod ||
      approvalPendingPeriod ||
      hubData?.current_period ||
      fallback
    );
  }, [approvalPendingPeriod, hubData?.current_period, leaderboardPeriod]);

  /** Refresh draft + approval-pending leaderboards and cycle metadata. */
  const refreshLeaderboardWorkflow = useCallback(async () => {
    const period = resolveLeaderboardPeriod();
    const cyclesResult = await dispatch(fetchCycles());
    await Promise.all([
      loadLeaderboard(period),
      loadApprovalPendingLeaderboard(period),
    ]);
    if (fetchCycles.fulfilled.match(cyclesResult)) {
      setCycles(cyclesResult.payload as CycleHistoryRow[]);
    }
  }, [
    dispatch,
    loadApprovalPendingLeaderboard,
    loadLeaderboard,
    resolveLeaderboardPeriod,
  ]);

  const load = useCallback(async () => {
    setLoading(true);
    const [cyclesResult, hubResult] = await Promise.all([
      dispatch(fetchCycles()),
      dispatch(fetchDiscoveryHub()),
    ]);

    if (fetchCycles.fulfilled.match(cyclesResult)) {
      setCycles(cyclesResult.payload as CycleHistoryRow[]);
    } else {
      setCycles([]);
    }

    if (fetchDiscoveryHub.fulfilled.match(hubResult)) {
      const hub = hubResult.payload;
      setHubData(hub);

      const lbPeriod = hub?.current_period || periodForLaunch("quarterly");
      const launchContextResult = await dispatch(fetchLaunchContext(lbPeriod));
      if (fetchLaunchContext.fulfilled.match(launchContextResult)) {
        setLaunchContext(launchContextResult.payload as LaunchContextPayload);
      }
      const [leaderboardResult, approvalPendingResult] = await Promise.all([
        dispatch(fetchLeaderboard({ period: lbPeriod })),
        dispatch(fetchApprovalPendingLeaderboard(lbPeriod)),
      ]);
      let draftPayload: LeaderboardPayload | null = null;
      let approvalPayload: LeaderboardPayload | null = null;
      if (fetchLeaderboard.fulfilled.match(leaderboardResult)) {
        draftPayload = leaderboardResult.payload as LeaderboardPayload;
        setDraftLeaderboard(draftPayload);
        setLeaderboardPeriod(lbPeriod);
      } else {
        toast.error(
          (leaderboardResult.payload as string) || "Failed to load leaderboard"
        );
        draftPayload = { period: lbPeriod, publication_state: null, rows: [] };
        setDraftLeaderboard(draftPayload);
        setLeaderboardPeriod(lbPeriod);
      }
      if (
        fetchApprovalPendingLeaderboard.fulfilled.match(approvalPendingResult)
      ) {
        approvalPayload = approvalPendingResult.payload as LeaderboardPayload;
        setApprovalPendingLeaderboard(approvalPayload);
        setApprovalPendingPeriod(lbPeriod);
      } else {
        toast.error(
          (approvalPendingResult.payload as string) ||
            "Failed to load approval-pending leaderboard"
        );
        approvalPayload = {
          period: lbPeriod,
          publication_state: null,
          rows: [],
        };
        setApprovalPendingLeaderboard(approvalPayload);
        setApprovalPendingPeriod(lbPeriod);
      }
      const isAssignedApprover =
        isAssignedLeaderboardApproverForUser(draftPayload, currentUserId) ||
        isAssignedLeaderboardApproverForUser(approvalPayload, currentUserId);
      if (isConfiguredApprover || isAssignedApprover) {
        await loadExecutiveSummary();
      }
    } else {
      setHubData(null);
      toast.error(
        (hubResult.payload as string) || "Failed to load Discovery Hub"
      );
    }
    setLoading(false);
  }, [currentUserId, dispatch, isConfiguredApprover, loadExecutiveSummary]);

  const refreshLaunchContext = useCallback(
    async (period?: string) => {
      const resolved =
        period ?? periodForLaunch(poll1FrequencyFromConfig(hubConfig));
      const result = await dispatch(fetchLaunchContext(resolved));
      if (fetchLaunchContext.fulfilled.match(result)) {
        setLaunchContext(result.payload as LaunchContextPayload);
        return result.payload as LaunchContextPayload;
      }
      return null;
    },
    [dispatch, hubConfig]
  );

  useEffect(() => {
    load();
  }, [load]);

  const hasApproverHubAccess = isConfiguredApprover;

  const executiveSummaryRequested = useRef(false);

  const isAssignedApproverView = useMemo(() => {
    if (!currentUserId) return false;
    return (
      isAssignedLeaderboardApproverForUser(
        approvalPendingLeaderboard,
        currentUserId
      ) || isAssignedLeaderboardApproverForUser(draftLeaderboard, currentUserId)
    );
  }, [approvalPendingLeaderboard, currentUserId, draftLeaderboard]);

  useEffect(() => {
    if (
      isDiscoveryHubTab(hubFromUrl, hasApproverHubAccess) &&
      hubFromUrl !== activeHub
    ) {
      setActiveHub(hubFromUrl);
    }
  }, [hubFromUrl, activeHub, hasApproverHubAccess]);

  const setHub = useCallback(
    (hub: DiscoveryHubTab) => {
      setActiveHub(hub);
      const next = new URLSearchParams(searchParams);
      next.set("tab", "discovery-hub");
      next.set("hub", hub);
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    if (loading) return;
    if (
      !hasApproverHubAccess &&
      isDiscoveryHubTab(activeHub, true) &&
      !isDiscoveryHubTab(activeHub, false)
    ) {
      setHub("ongoing");
    }
  }, [activeHub, hasApproverHubAccess, loading, setHub]);

  useEffect(() => {
    if (!hasApproverHubAccess || executiveSummaryRequested.current) return;
    if (
      activeHub === "executive-summary" &&
      !executiveSummary &&
      !executiveSummaryLoading
    ) {
      executiveSummaryRequested.current = true;
      void loadExecutiveSummary();
    }
  }, [
    activeHub,
    executiveSummary,
    executiveSummaryLoading,
    hasApproverHubAccess,
    loadExecutiveSummary,
  ]);

  const currentQuarterPeriod = useMemo(() => periodForLaunch("quarterly"), []);

  const pollGroups: any[] = hubData?.poll_groups ?? [];
  const hasActivePolls = pollGroups.length > 0;

  const ongoingPollGroups = useMemo(
    () => pollGroups.filter((g: any) => groupMatchesHubTab(g, "ongoing")),
    [pollGroups]
  );

  const allOngoingPendingReminders = useMemo(
    () =>
      ongoingPollGroups.flatMap((g: any) =>
        (g.ongoing_sponsors ?? []).filter(sponsorHasPendingResponse)
      ),
    [ongoingPollGroups]
  );

  const allOngoingPoll2PendingReminders = useMemo(
    () =>
      ongoingPollGroups.flatMap((g: any) =>
        (g.ongoing_team_poll2 ?? []).filter(teamMemberHasPendingResponse)
      ),
    [ongoingPollGroups]
  );

  const totalOngoingPendingReminders =
    allOngoingPendingReminders.length + allOngoingPoll2PendingReminders.length;

  const lateWindowPollGroups = useMemo(
    () => pollGroups.filter((g: any) => groupMatchesHubTab(g, "late-window")),
    [pollGroups]
  );

  const allLateWindowPendingReminders = useMemo(
    () =>
      lateWindowPollGroups.flatMap((g: any) =>
        (g.late_window_sponsors ?? []).filter(sponsorHasPendingResponse)
      ),
    [lateWindowPollGroups]
  );

  const allLateWindowPoll2PendingReminders = useMemo(
    () =>
      lateWindowPollGroups.flatMap((g: any) =>
        (g.late_window_team_poll2 ?? []).filter(teamMemberHasPendingResponse)
      ),
    [lateWindowPollGroups]
  );

  const totalLateWindowPendingReminders =
    allLateWindowPendingReminders.length +
    allLateWindowPoll2PendingReminders.length;

  const completedPollGroups = useMemo(() => {
    const source = hubData?.completed_poll_groups ?? pollGroups;
    return source.filter((g: any) => groupMatchesHubTab(g, "completed"));
  }, [hubData?.completed_poll_groups, pollGroups]);

  const completedPollGroupsByPeriod = useMemo(
    () => groupPollGroupsByPeriod(completedPollGroups),
    [completedPollGroups]
  );

  const hasCompletedPolls = completedPollGroups.length > 0;

  const togglePollExpand = useCallback((cycleId: string) => {
    setExpandedPollIds(prev => ({ ...prev, [cycleId]: !prev[cycleId] }));
  }, []);

  const period = hubData?.current_period || currentQuarterPeriod;

  const currentQuarterCompletedPolls = useMemo(
    () => completedPollGroups.filter((g: any) => g.period === period),
    [completedPollGroups, period]
  );

  const historicalCompletedPollGroupsByPeriod = useMemo(
    () =>
      completedPollGroupsByPeriod.filter(
        ({ period: groupPeriod }) => groupPeriod !== period
      ),
    [completedPollGroupsByPeriod, period]
  );

  const cycleExistsInList = useMemo(
    () => cycles.some((c: any) => c.period === currentQuarterPeriod),
    [cycles, currentQuarterPeriod]
  );
  const tabBreakdown = useMemo(() => {
    if (activeHub === "completed") {
      return buildTabSponsorBreakdown(
        currentQuarterCompletedPolls,
        "completed"
      );
    }
    if (activeHub === "ongoing" || activeHub === "late-window") {
      return buildTabSponsorBreakdown(pollGroups, activeHub);
    }
    return {};
  }, [pollGroups, currentQuarterCompletedPolls, activeHub]);

  const sponsorReminderKey = (sponsorId: string, cycleId?: string) =>
    `sponsor:${sponsorId}:${cycleId || ""}`;

  const teamReminderKey = (
    teamMemberId: string,
    sponsorId: string,
    cycleId: string
  ) => `team:${teamMemberId}:${sponsorId}:${cycleId}`;

  const executeSponsorReminder = async (
    sponsorId: string,
    cycleId?: string
  ) => {
    const loadingKey = sponsorReminderKey(sponsorId, cycleId);
    setReminderLoadingId(loadingKey);
    try {
      const result = await dispatch(
        sendSponsorReminder({ sponsorId, cycleId })
      );
      if (sendSponsorReminder.fulfilled.match(result)) {
        toast.success("Reminder sent successfully");
        await load();
      } else {
        toast.error((result.payload as string) || "Reminder failed");
      }
    } catch (e: unknown) {
      toast.error(
        (e as any)?.response?.data?.detail ||
          (e as any)?.message ||
          "Reminder failed"
      );
    } finally {
      setReminderLoadingId(prev => (prev === loadingKey ? null : prev));
    }
  };

  const executePoll2Reminder = async (
    teamMemberId: string,
    sponsorId: string,
    cycleId: string
  ) => {
    const loadingKey = teamReminderKey(teamMemberId, sponsorId, cycleId);
    setReminderLoadingId(loadingKey);
    try {
      const result = await dispatch(
        sendPoll2ReminderThunk({ teamMemberId, sponsorId, cycleId })
      );
      if (sendPoll2ReminderThunk.fulfilled.match(result)) {
        toast.success("Reminder sent successfully");
        await load();
      } else {
        toast.error((result.payload as string) || "Reminder failed");
      }
    } catch (e: unknown) {
      toast.error(
        (e as any)?.response?.data?.detail ||
          (e as any)?.message ||
          "Reminder failed"
      );
    } finally {
      setReminderLoadingId(prev => (prev === loadingKey ? null : prev));
    }
  };

  const executeBulkReminders = async (
    sponsorRows: any[] = [],
    teamRows: TeamPoll2Row[] = []
  ) => {
    setBulkReminderLoading(true);
    try {
      const bulkResult = await dispatch(
        sendBulkRemindersThunk({
          poll1_targets: sponsorRows.map(row => ({
            sponsor_id: row.sponsor_id,
            cycle_id: row.cycle_id || undefined,
          })),
          poll2_targets: teamRows
            .filter(row => row.cycle_id)
            .map(row => ({
              team_member_id: row.team_member_id,
              sponsor_id: row.sponsor_id,
              cycle_id: row.cycle_id,
            })),
        })
      );
      if (sendBulkRemindersThunk.rejected.match(bulkResult)) {
        toast.error((bulkResult.payload as string) || "Bulk reminder failed");
        return;
      }
      const sent =
        (bulkResult.payload as { reminders_sent?: number })?.reminders_sent ??
        0;
      if (sent > 0) {
        toast.success("Reminders sent successfully");
      } else {
        toast.error("No reminders were sent");
      }
      setBulkReminderLoading(false);
      setHubRefreshing(true);
      await load();
    } catch (e: unknown) {
      toast.error(
        (e as any)?.response?.data?.detail ||
          (e as any)?.message ||
          "Bulk reminder failed"
      );
    } finally {
      setBulkReminderLoading(false);
      setHubRefreshing(false);
    }
  };

  const requestSponsorReminder = (sponsorId: string, cycleId?: string) => {
    setPendingConfirm({
      type: "sponsor_reminder",
      sponsorId,
      cycleId,
      label: "Send a reminder to this sponsor?",
    });
  };

  const requestPoll2Reminder = (
    teamMemberId: string,
    sponsorId: string,
    cycleId: string,
    teamMemberName?: string
  ) => {
    setPendingConfirm({
      type: "poll2_reminder",
      teamMemberId,
      sponsorId,
      cycleId,
      label: teamMemberName
        ? `Send a reminder to ${teamMemberName}?`
        : "Send a reminder to this team member?",
    });
  };

  const requestBulkReminders = (
    sponsorRows: any[] = [],
    teamRows: TeamPoll2Row[] = []
  ) => {
    const count = sponsorRows.length + teamRows.length;
    setPendingConfirm({
      type: "bulk_reminder",
      sponsorRows,
      teamRows,
      label: `Send reminders to ${count} participant${count === 1 ? "" : "s"}?`,
    });
  };

  const executeSaveDraftLeaderboard = async () => {
    if (selectedCompletedCycleIds.size === 0) return;
    setSavingDraftLeaderboard(true);
    try {
      const saveResult = await dispatch(
        saveDraftLeaderboard({
          period,
          cycleIds: Array.from(selectedCompletedCycleIds),
        })
      );
      if (saveDraftLeaderboard.rejected.match(saveResult)) {
        toast.error(
          (saveResult.payload as string) || "Failed to save draft leaderboard"
        );
        return;
      }
      const saved = saveResult.payload as SaveDraftLeaderboardResult;
      const addedCount = saved.added_cycle_ids?.length ?? 0;
      setSelectedCompletedCycleIds(new Set());
      if (addedCount === 0) {
        toast.info("Selected poll(s) are already in the draft leaderboard.");
        await load();
        return;
      }
      if (saved.updated_pending_approval) {
        toast.success(
          addedCount === 1
            ? "Draft leaderboard updated. The assigned approver has been notified."
            : `${addedCount} poll(s) added to the draft leaderboard. The assigned approver has been notified.`
        );
      } else {
        toast.success(
          addedCount === 1
            ? "Saved to draft leaderboard"
            : `${addedCount} poll(s) saved to draft leaderboard`
        );
      }
      await load();
      setHub("draft-leaderboard");
    } catch (e: unknown) {
      toast.error(
        (e as any)?.response?.data?.detail ||
          (e as any)?.message ||
          "Failed to save draft leaderboard"
      );
    } finally {
      setSavingDraftLeaderboard(false);
    }
  };

  const handleConfirmAction = async () => {
    if (!pendingConfirm) return;
    setConfirmLoading(true);
    try {
      if (pendingConfirm.type === "sponsor_reminder") {
        await executeSponsorReminder(
          pendingConfirm.sponsorId,
          pendingConfirm.cycleId
        );
      } else if (pendingConfirm.type === "poll2_reminder") {
        await executePoll2Reminder(
          pendingConfirm.teamMemberId,
          pendingConfirm.sponsorId,
          pendingConfirm.cycleId
        );
      } else if (pendingConfirm.type === "bulk_reminder") {
        await executeBulkReminders(
          pendingConfirm.sponsorRows,
          pendingConfirm.teamRows
        );
      } else if (pendingConfirm.type === "save_draft") {
        await executeSaveDraftLeaderboard();
      }
      setPendingConfirm(null);
    } finally {
      setConfirmLoading(false);
    }
  };

  const VIEW_DRILLDOWN_DISABLED_TITLE =
    "Available when all account responses are complete";

  const renderPollProgressActions = (
    showReminders: boolean,
    canRemind: boolean,
    remindLoading: boolean,
    onRemind: () => void,
    onView: () => void,
    canView: boolean
  ) => (
    <div className={POLL_PROGRESS_ACTIONS}>
      {showReminders ? (
        <Button
          variant="secondary"
          className="text-sm py-1 px-2"
          disabled={!canRemind || bulkReminderBusy}
          loading={remindLoading}
          onClick={onRemind}
        >
          Send Reminder
        </Button>
      ) : null}
      {canViewDrilldown ? (
        <Button
          variant="clear"
          className="text-sm py-1 px-2 text-purple-700 font-semibold hover:text-purple-900"
          disabled={!canView}
          title={!canView ? VIEW_DRILLDOWN_DISABLED_TITLE : undefined}
          onClick={onView}
        >
          View
        </Button>
      ) : null}
    </div>
  );

  const poll1Cadence = useMemo(
    () => poll1FrequencyFromConfig(hubConfig),
    [hubConfig]
  );
  const poll2Cadence = useMemo(
    () => poll2FrequencyFromConfig(hubConfig),
    [hubConfig]
  );
  const poll1AudienceOptions = useMemo(
    () => launchContext?.poll1_audience_options ?? [],
    [launchContext]
  );

  const poll2AudienceOptions = useMemo(
    () => launchContext?.poll2_audience_options ?? [],
    [launchContext]
  );

  const launchDefaultsFromConfig =
    useCallback((): DiscoveryHubLaunchDefaults | null => {
      return hubConfig?.launch_defaults ?? null;
    }, [hubConfig]);

  const resetLaunchForm = useCallback(() => {
    setLaunchForm(
      defaultLaunchFormState(
        poll1FrequencyFromConfig(hubConfig),
        poll2FrequencyFromConfig(hubConfig),
        launchDefaultsFromConfig()
      )
    );
  }, [hubConfig, launchDefaultsFromConfig]);

  const remountLaunchModal = useCallback(() => {
    setLaunchModalKey(k => k + 1);
  }, []);

  const closeLaunchModal = useCallback(() => {
    if (launchSubmitting) return;
    setLaunchOpen(false);
    resetLaunchForm();
    remountLaunchModal();
  }, [launchSubmitting, resetLaunchForm, remountLaunchModal]);

  const openLaunchModal = async () => {
    if (
      launchContext &&
      !launchContext.can_launch &&
      !hasParticipantLaunchBlocks(launchContext)
    ) {
      toast.error(
        launchContext.block_reason || "Cannot launch a poll right now."
      );
      return;
    }
    setLaunchModalOpening(true);
    try {
      let config = hubConfig;
      if (!config) {
        try {
          const configResult = await dispatch(fetchEngagementConfig());
          config = fetchEngagementConfig.fulfilled.match(configResult)
            ? (configResult.payload ?? null)
            : null;
          setHubConfig(config);
        } catch {
          config = null;
        }
      }
      const p1 = poll1FrequencyFromConfig(config);
      const p2 = poll2FrequencyFromConfig(config);
      const launchPeriod = periodForLaunch(p1);
      const ctx = (await refreshLaunchContext(launchPeriod)) ?? launchContext;
      if (ctx && !ctx.can_launch && !hasParticipantLaunchBlocks(ctx)) {
        toast.error(ctx.block_reason || "Cannot launch a poll right now.");
        return;
      }
      const p1Audience = firstEnabledAudience(ctx?.poll1_audience_options);
      const p2Audience = firstEnabledAudience(ctx?.poll2_audience_options);
      setLaunchForm({
        ...defaultLaunchFormState(p1, p2, config?.launch_defaults ?? null),
        poll1_enabled: ctx?.poll1_can_launch === true,
        poll2_enabled: ctx?.poll2_can_launch === true,
        poll1_target_audience: p1Audience,
        poll2_target_audience: p2Audience,
      });
      remountLaunchModal();
      setLaunchOpen(true);
    } finally {
      setLaunchModalOpening(false);
    }
  };

  const handleLaunchFormChange = (patch: Partial<LaunchPollFormState>) => {
    setLaunchForm(prev => ({ ...prev, ...patch }));
  };

  const handlePollTypeToggle = (
    pollKey: "poll1" | "poll2",
    enabled: boolean
  ) => {
    if (
      enabled &&
      pollKey === "poll1" &&
      launchContext &&
      !launchContext.poll1_can_launch
    ) {
      if (
        launchContext.poll1_participant_block ||
        launchContext.poll2_can_launch
      ) {
        return;
      }
      toast.error(
        launchContext.poll1_block_reason ||
          "Poll 1 has already been conducted for all audiences in this cycle. You can create the next Poll 1 in the next cycle."
      );
      return;
    }
    if (
      enabled &&
      pollKey === "poll2" &&
      launchContext &&
      !launchContext.poll2_can_launch
    ) {
      if (launchContext.poll2_participant_block) {
        return;
      }
      toast.error(
        launchContext.poll2_block_reason ||
          "Poll 2 is not available for any remaining audiences in this cycle."
      );
      return;
    }
    if (pollKey === "poll1") {
      handleLaunchFormChange({ poll1_enabled: enabled });
    } else {
      handleLaunchFormChange({ poll2_enabled: enabled });
    }
  };

  const handleLaunch = async () => {
    const poll1Launchable =
      launchForm.poll1_enabled && (launchContext?.poll1_can_launch ?? false);
    const poll2Launchable =
      launchForm.poll2_enabled && (launchContext?.poll2_can_launch ?? false);

    if (!poll1Launchable && !poll2Launchable) {
      toast.error("Select at least one poll type.");
      return;
    }
    const launchPeriod = periodForLaunch(poll1Cadence);
    const poll2Period = periodForLaunch(poll2Cadence);
    const primaryDue = poll1Launchable
      ? launchForm.poll1_due_date
      : launchForm.poll2_due_date;

    if (poll1Launchable) {
      if (!launchForm.poll1_due_date) {
        toast.error("Poll 1 due date is required");
        return;
      }
      if (isPastDueDate(launchForm.poll1_due_date)) {
        toast.error("Due date must be today or a future date");
        return;
      }
      if (
        launchContext?.poll1_cycle_end_date &&
        launchForm.poll1_due_date > launchContext.poll1_cycle_end_date
      ) {
        toast.error(
          `Due date exceeds the configured cycle end date (${launchContext.poll1_cycle_end_date}).`
        );
        return;
      }
    }
    if (poll2Launchable) {
      if (!launchForm.poll2_due_date) {
        toast.error("Poll 2 due date is required");
        return;
      }
      if (isPastDueDate(launchForm.poll2_due_date)) {
        toast.error("Due date must be today or a future date");
        return;
      }
      if (
        launchContext?.poll2_cycle_end_date &&
        launchForm.poll2_due_date > launchContext.poll2_cycle_end_date
      ) {
        toast.error(
          `Due date exceeds the configured cycle end date (${launchContext.poll2_cycle_end_date}).`
        );
        return;
      }
    }

    const pollName = composePollNameFromSuffix(
      launchPeriod,
      launchForm.poll_name_suffix
    );
    if (
      isPollNameOnlyDefaultQuarter(launchForm.poll_name_suffix, launchPeriod)
    ) {
      toast.error("Please enter a poll name.");
      return;
    }
    if (poll1Launchable) {
      const selected = launchContext?.poll1_audience_options?.find(
        opt => opt.value === launchForm.poll1_target_audience
      );
      if (selected?.disabled) {
        toast.error(
          selected.disabled_reason ||
            "This audience has already been used for Poll 1 in the current cycle."
        );
        return;
      }
    }
    if (poll2Launchable) {
      const selected = launchContext?.poll2_audience_options?.find(
        opt => opt.value === launchForm.poll2_target_audience
      );
      if (selected?.disabled) {
        toast.error(
          selected.disabled_reason ||
            "This audience has already been used for Poll 2 in the current cycle."
        );
        return;
      }
    }
    setLaunchSubmitting(true);
    try {
      const launchResult = await dispatch(
        launchPoll({
          period: launchPeriod,
          name: pollName.trim(),
          due_date: primaryDue,
          poll1_due_date: poll1Launchable
            ? launchForm.poll1_due_date
            : undefined,
          poll2_due_date: poll2Launchable
            ? launchForm.poll2_due_date
            : undefined,
          reminder_cadence: launchForm.reminder_cadence,
          poll1_enabled: poll1Launchable,
          poll2_enabled: poll2Launchable,
          target_audience: poll1Launchable
            ? launchForm.poll1_target_audience
            : launchForm.poll2_target_audience,
          poll2_target_audience: poll2Launchable
            ? launchForm.poll2_target_audience
            : undefined,
          poll2_period: poll2Launchable ? poll2Period : undefined,
        })
      );
      if (launchPoll.rejected.match(launchResult)) {
        toast.error((launchResult.payload as string) || "Launch failed");
        return;
      }
      const res = launchResult.payload as { created?: boolean };
      if (res?.created === false) {
        toast.success(`Poll "${pollName.trim()}" updated for ${launchPeriod}`);
      } else {
        const parts = [
          poll1Launchable ? "Poll 1" : null,
          poll2Launchable ? "Poll 2" : null,
        ].filter(Boolean);
        toast.success(`${parts.join(" and ")} launched`);
      }
      setLaunchOpen(false);
      resetLaunchForm();
      remountLaunchModal();
      setExpandedPollIds({});
      setHub(res?.created === false ? "cycles" : "ongoing");
      setHubRefreshing(true);
      try {
        await load();
      } finally {
        setHubRefreshing(false);
      }
    } catch (e: unknown) {
      toast.error(
        (e as any)?.response?.data?.detail ||
          (e as any)?.message ||
          "Launch failed"
      );
    } finally {
      setLaunchSubmitting(false);
    }
  };

  const hubTabs = useMemo(() => {
    const tabs = [
      {
        id: "cycles",
        label: "Poll History",
        icon: <List size={18} />,
      },
      {
        id: "ongoing",
        label: "Ongoing Polls",
        icon: <Clock size={18} />,
      },
      {
        id: "late-window",
        label: "Late Polls",
        icon: <Timer size={18} />,
      },
      {
        id: "completed",
        label: "Completed Polls",
        icon: <CheckCircle size={18} />,
      },
      {
        id: "draft-leaderboard",
        label: "Draft Leaderboard",
        icon: <BarChart3 size={18} />,
      },
    ];
    if (hasApproverHubAccess) {
      tabs.push(
        {
          id: "approval-pending",
          label: "Approval Pending",
          icon: <ClipboardCheck size={18} />,
        },
        {
          id: "executive-summary",
          label: "Executive Summary",
          icon: <LineChart size={18} />,
        }
      );
    }
    return tabs;
  }, [hasApproverHubAccess]);

  const renderStatusChips = (
    breakdown: Record<string, number> = tabBreakdown
  ) => {
    const chipTotal = breakdown.total || 0;
    const chipPct = (n: number) =>
      chipTotal > 0 ? Math.round((n / chipTotal) * 100) : 0;
    return (
      <div className={STATUS_CHIP_ROW}>
        <div className={pollStatusChipClass("notstarted")}>
          <div className={statusChipLabelClass}>Not started</div>
          <div className={statusChipValueClass}>
            {breakdown.not_started ?? 0}
            <span className={statusChipValueUnitClass}>
              {chipPct(breakdown.not_started ?? 0)}%
            </span>
          </div>
        </div>
        <div className={pollStatusChipClass("inprogress")}>
          <div className={statusChipLabelClass}>In progress</div>
          <div className={statusChipValueClass}>
            {breakdown.in_progress ?? 0}
            <span className={statusChipValueUnitClass}>
              {chipPct(breakdown.in_progress ?? 0)}%
            </span>
          </div>
        </div>
        <div className={pollStatusChipClass("ontime")}>
          <div className={statusChipLabelClass}>On-time</div>
          <div className={statusChipValueClass}>
            {breakdown.submitted_on_time ?? 0}
            <span className={statusChipValueUnitClass}>
              {chipPct(breakdown.submitted_on_time ?? 0)}%
            </span>
          </div>
        </div>
        <div className={pollStatusChipClass("late")}>
          <div className={statusChipLabelClass}>Late</div>
          <div className={statusChipValueClass}>
            {breakdown.submitted_late ?? 0}
            <span className={statusChipValueUnitClass}>
              {chipPct(breakdown.submitted_late ?? 0)}%
            </span>
          </div>
        </div>
        <div className={pollStatusChipClass("overdue")}>
          <div className={statusChipLabelClass}>Overdue</div>
          <div className={statusChipValueClass}>
            {breakdown.overdue ?? 0}
            <span className={statusChipValueUnitClass}>
              {chipPct(breakdown.overdue ?? 0)}%
            </span>
          </div>
        </div>
      </div>
    );
  };

  const goToDrilldown = (
    sponsorId: string,
    cycleId?: string,
    context?: {
      source?: "poll2";
      teamMemberId?: string;
      teamMemberName?: string;
      sponsorName?: string;
    }
  ) => {
    const returnTo = encodeURIComponent(
      `${location.pathname}${location.search}`
    );
    const q = new URLSearchParams({ sponsorId, returnTo });
    if (cycleId) q.set("cycleId", cycleId);
    if (context?.source === "poll2") {
      q.set("source", "poll2");
      if (context.teamMemberId) q.set("teamMemberId", context.teamMemberId);
      if (context.teamMemberName)
        q.set("teamMemberName", context.teamMemberName);
      if (context.sponsorName) q.set("sponsorName", context.sponsorName);
    }
    navigate(`${withOrg("/sponsor-drilldown")}?${q.toString()}`);
  };

  const renderTeamFeedbackTable = (
    rows: TeamPoll2Row[],
    emptyTitle: string,
    emptyHint: string,
    cycleId?: string,
    showReminders = false
  ) => {
    if (rows.length === 0) {
      return (
        <EngagementEmptyPanel
          variant="hub-compact"
          icon={CheckCircle}
          title={emptyTitle}
          hint={emptyHint}
          className="mt-3"
        />
      );
    }
    return (
      <div className={`${POLL_PROGRESS_TABLE} mt-3`}>
        <Table
          columns={[
            { key: "team_member_name", title: "Team Member" },
            { key: "sponsor_name", title: "Sponsor" },
            {
              key: "completed_accounts",
              title: "Account Responses",
              render: (_v: unknown, r: TeamPoll2Row) => {
                const label = teamFeedbackStatusLabel(r);
                if (label === "Complete") {
                  return (
                    <span className="text-green-700 font-medium">
                      {r.completed_accounts ?? 0} / {r.total_accounts ?? 0} ·
                      Complete
                    </span>
                  );
                }
                return `${r.completed_accounts ?? 0} / ${r.total_accounts ?? 0}`;
              },
            },
            {
              key: "actions",
              title: "Action",
              render: (_v: unknown, r: TeamPoll2Row) => {
                const cid = cycleId || r.cycle_id;
                const canRemind = teamMemberHasPendingResponse(r);
                const remindKey = cid
                  ? teamReminderKey(r.team_member_id, r.sponsor_id, cid)
                  : "";
                const canView = sponsorPoll1IsComplete(r);
                return renderPollProgressActions(
                  showReminders,
                  canRemind,
                  remindKey !== "" && reminderLoadingId === remindKey,
                  () => {
                    if (cid) {
                      requestPoll2Reminder(r.team_member_id, r.sponsor_id, cid);
                    }
                  },
                  () =>
                    goToDrilldown(r.sponsor_id, cid, {
                      source: "poll2",
                      teamMemberId: r.team_member_id,
                      teamMemberName: r.team_member_name,
                      sponsorName: r.sponsor_name,
                    }),
                  canView
                );
              },
            },
          ]}
          data={rows}
        />
      </div>
    );
  };

  const renderOverdueTable = (
    rows: any[],
    emptyTitle: string,
    emptyHint: string,
    groupCycleId?: string,
    showReminders = true,
    variant: "sponsor" | "team-poll2" = "sponsor"
  ) => {
    if (rows.length === 0) {
      return (
        <EngagementEmptyPanel
          variant="hub-compact"
          icon={Timer}
          title={emptyTitle}
          hint={emptyHint}
          className="mt-3"
        />
      );
    }

    const isTeamPoll2 = variant === "team-poll2";
    const rowHasPending = isTeamPoll2
      ? teamMemberHasPendingResponse
      : sponsorHasPendingResponse;

    return (
      <div className="bg-white border border-gray-200 rounded-lg mb-4 overflow-hidden mt-3">
        <table className={OVERDUE_TABLE}>
          <colgroup>
            {isTeamPoll2 ? (
              <>
                <col className="w-[14%]" />
                <col className="w-[14%]" />
                <col className="w-[11%]" />
                <col className="w-[9%]" />
                <col className="w-[13%]" />
                <col className="w-[9%]" />
                <col className="w-[30%]" />
              </>
            ) : (
              <>
                <col className="w-[22%]" />
                <col className="w-[11%]" />
                <col className="w-[9%]" />
                <col className="w-[14%]" />
                <col className="w-[9%]" />
                <col className="w-[35%]" />
              </>
            )}
          </colgroup>
          <thead>
            <tr>
              {isTeamPoll2 ? (
                <>
                  <th className="text-left whitespace-nowrap">Team Member</th>
                  <th className="text-left whitespace-nowrap">Sponsor</th>
                </>
              ) : (
                <th className="text-left whitespace-nowrap">Sponsor</th>
              )}
              <th className="text-center whitespace-nowrap">
                Account Responses
              </th>
              <th className="text-center whitespace-nowrap">Days Overdue</th>
              <th className="text-center whitespace-nowrap">Last Reminded</th>
              <th className="text-center whitespace-nowrap">Reminders Sent</th>
              <th className="text-center whitespace-nowrap">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row: any) => {
              const rc = row.reminder_count ?? 0;
              const badge = rc === 0 ? "zero" : rc <= 2 ? "low" : "high";
              const lastAt = row.last_reminded_at;
              const rowCycleId = row.cycle_id || groupCycleId;
              const remindKey = isTeamPoll2
                ? teamReminderKey(
                    row.team_member_id,
                    row.sponsor_id,
                    rowCycleId
                  )
                : sponsorReminderKey(row.sponsor_id, rowCycleId);
              const done = row.completed_accounts ?? 0;
              const total = row.total_accounts ?? 0;
              const responsesComplete = isTeamPoll2
                ? total > 0 && done >= total
                : sponsorPoll1IsComplete(row);
              const rowKey = isTeamPoll2
                ? `${row.cycle_id || groupCycleId || ""}-${row.team_member_id}-${row.sponsor_id}`
                : `${row.cycle_id || groupCycleId || ""}-${row.sponsor_id}`;
              return (
                <tr key={rowKey}>
                  {isTeamPoll2 ? (
                    <>
                      <td
                        className={OVERDUE_NAME_CELL}
                        title={row.team_member_name || row.team_member_id}
                      >
                        {row.team_member_name || row.team_member_id}
                      </td>
                      <td
                        className={OVERDUE_NAME_CELL}
                        title={row.sponsor_name || row.sponsor_id}
                      >
                        {row.sponsor_name || row.sponsor_id}
                      </td>
                    </>
                  ) : (
                    <td
                      className={OVERDUE_NAME_CELL}
                      title={row.sponsor_name || row.sponsor_id}
                    >
                      {row.sponsor_name || row.sponsor_id}
                    </td>
                  )}
                  <td className={OVERDUE_METRIC_CELL}>
                    {responsesComplete ? (
                      <span className="text-green-700 font-medium">
                        {done} / {total} · Complete
                      </span>
                    ) : (
                      `${done} / ${total}`
                    )}
                  </td>
                  <td className={OVERDUE_METRIC_CELL}>
                    <span className="font-bold text-red-900">
                      {row.days_overdue ?? 0}d
                    </span>
                  </td>
                  <td className={OVERDUE_METRIC_CELL}>
                    <span
                      className={
                        !lastAt ? "text-amber-700 font-semibold" : undefined
                      }
                    >
                      {fmtRelative(lastAt)}
                    </span>
                  </td>
                  <td className={OVERDUE_METRIC_CELL}>
                    <span className={reminderBadgeClass(badge)}>{rc}</span>
                  </td>
                  <td className={OVERDUE_ACTION_CELL}>
                    <div className={`${POLL_PROGRESS_ACTIONS} justify-center`}>
                      {showReminders && (
                        <Button
                          variant="secondary"
                          className="text-sm py-1 px-2"
                          disabled={!rowHasPending(row) || bulkReminderBusy}
                          loading={reminderLoadingId === remindKey}
                          onClick={() => {
                            if (isTeamPoll2) {
                              if (rowCycleId) {
                                requestPoll2Reminder(
                                  row.team_member_id,
                                  row.sponsor_id,
                                  rowCycleId
                                );
                              }
                            } else {
                              requestSponsorReminder(
                                row.sponsor_id,
                                rowCycleId
                              );
                            }
                          }}
                        >
                          Send Reminder
                        </Button>
                      )}
                      {canViewDrilldown ? (
                        <Button
                          variant="clear"
                          className="text-sm py-1 px-2 text-purple-700 font-semibold hover:text-purple-900"
                          disabled={!responsesComplete}
                          title={
                            !responsesComplete
                              ? VIEW_DRILLDOWN_DISABLED_TITLE
                              : undefined
                          }
                          onClick={() =>
                            isTeamPoll2
                              ? goToDrilldown(row.sponsor_id, rowCycleId, {
                                  source: "poll2",
                                  teamMemberId: row.team_member_id,
                                  teamMemberName: row.team_member_name,
                                  sponsorName: row.sponsor_name,
                                })
                              : goToDrilldown(row.sponsor_id, rowCycleId)
                          }
                        >
                          View
                        </Button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderSponsorProgressTable = (
    rows: any[],
    emptyTitle: string,
    emptyHint: string,
    cycleId?: string,
    showReminders = false
  ) => {
    if (rows.length === 0) {
      return (
        <EngagementEmptyPanel
          variant="hub-compact"
          icon={ClipboardCheck}
          title={emptyTitle}
          hint={emptyHint}
          className="mt-3"
        />
      );
    }
    return (
      <div className={`${POLL_PROGRESS_TABLE} mt-3`}>
        <Table
          columns={[
            { key: "sponsor_name", title: "Sponsor" },
            {
              key: "gl",
              title: "Theater",
              render: (_v: any, r: any) => `${r.theater || "—"}`,
            },
            {
              key: "completed_accounts",
              title: "Account Responses",
              render: (_v: any, r: any) => {
                const done = r.completed_accounts ?? 0;
                const total = r.total_accounts ?? 0;
                if (sponsorPoll1IsComplete(r)) {
                  return (
                    <span className="text-green-700 font-medium">
                      {done} / {total} · Complete
                    </span>
                  );
                }
                return `${done} / ${total}`;
              },
            },
            {
              key: "actions",
              title: "Action",
              render: (_v: any, r: any) => {
                const cid = cycleId || r.cycle_id;
                const canRemind = sponsorHasPendingResponse(r);
                const remindKey = sponsorReminderKey(r.sponsor_id, cid);
                return renderPollProgressActions(
                  showReminders,
                  canRemind,
                  reminderLoadingId === remindKey,
                  () => requestSponsorReminder(r.sponsor_id, cid),
                  () => goToDrilldown(r.sponsor_id, cid),
                  sponsorPoll1IsComplete(r)
                );
              },
            },
          ]}
          data={rows}
        />
      </div>
    );
  };

  const toggleCompletedCycleSelection = useCallback(
    (cycleId: string, checked: boolean) => {
      setSelectedCompletedCycleIds(prev => {
        const next = new Set(prev);
        if (checked) {
          next.add(cycleId);
        } else {
          next.delete(cycleId);
        }
        return next;
      });
    },
    []
  );

  const handleSaveDraftLeaderboard = () => {
    if (selectedCompletedCycleIds.size === 0) return;
    const count = selectedCompletedCycleIds.size;
    setPendingConfirm({
      type: "save_draft",
      label: `Save ${count} selected poll${count === 1 ? "" : "s"} to the draft leaderboard?`,
    });
  };

  const renderPollListLoading = () => (
    <HubPageLoading message="Refreshing polls…" compact className="mt-4" />
  );

  const renderPollCycleCards = (
    groupsToShow: any[],
    hubTab: "ongoing" | "late-window" | "completed",
    options?: {
      showReminders?: boolean;
      selectable?: boolean;
      selectablePeriod?: string;
    }
  ) => (
    <div className="flex flex-col gap-5">
      {groupsToShow.map((g: any) => {
        const cycleId = g.cycle_id || `${g.period}-${g.poll_name}`;
        const savedToDraft = !!g.included_in_draft_leaderboard;
        const pollFullyCompleted = g.hub_status === "completed";
        const canSelectForDraft =
          options?.selectable &&
          pollFullyCompleted &&
          !g.published_to_leaderboard &&
          (!options.selectablePeriod || g.period === options.selectablePeriod);
        return (
          <PollCycleCard
            key={cycleId}
            group={g}
            activeHub={hubTab}
            sponsorCount={countSponsorsForHubTab(g, hubTab)}
            isExpanded={!!expandedPollIds[cycleId]}
            onToggleExpand={() => togglePollExpand(cycleId)}
            selectable={canSelectForDraft}
            selected={selectedCompletedCycleIds.has(cycleId)}
            onSelectChange={checked =>
              toggleCompletedCycleSelection(cycleId, checked)
            }
            savedToDraft={savedToDraft}
            onExtendDueDate={
              hubTab === "ongoing"
                ? () =>
                    setExtendDueDateModal({
                      isOpen: true,
                      cycleId: g.cycle_id,
                      pollName: g.poll_name,
                      currentDueDate: g.due_date ?? "",
                      newDueDate: g.due_date ?? "",
                      reason: "",
                    })
                : undefined
            }
          >
            <PollAccordionContent
              group={g}
              hubTab={hubTab}
              cycleId={cycleId}
              showReminders={options?.showReminders}
              renderSponsorProgressTable={renderSponsorProgressTable}
              renderOverdueTable={renderOverdueTable}
              renderTeamFeedbackTable={renderTeamFeedbackTable}
            />
          </PollCycleCard>
        );
      })}
    </div>
  );

  const renderPollCycleList = (
    emptyTitle: string,
    emptyHint: string,
    hubTab: "ongoing" | "late-window" | "completed",
    options?: {
      showReminders?: boolean;
      selectable?: boolean;
      selectablePeriod?: string;
      loading?: boolean;
      groups?: any[];
    }
  ) => {
    if (options?.loading) {
      return renderPollListLoading();
    }

    const groupsToShow = sortPollGroupsByLaunchDate(
      options?.groups ??
        pollGroups.filter((g: any) => groupMatchesHubTab(g, hubTab))
    );

    if (groupsToShow.length === 0) {
      if (hubTab === "ongoing") {
        return (
          <SampleDataOverlay message="Preview of ongoing polls — launch a poll cycle to start collecting sponsor responses.">
            <SampleOngoingPollCards />
          </SampleDataOverlay>
        );
      }
      if (hubTab === "late-window") {
        return (
          <SampleDataOverlay message="Preview of late polls — overdue responses appear here during the late collection window.">
            <SampleLatePollCards />
          </SampleDataOverlay>
        );
      }
      return (
        <EngagementEmptyPanel
          variant="hub-compact"
          icon={List}
          title={emptyTitle}
          hint={emptyHint}
          className="mt-4"
        />
      );
    }

    return renderPollCycleCards(groupsToShow, hubTab, options);
  };

  const renderCompletedPollsByPeriod = (options?: { loading?: boolean }) => {
    if (options?.loading) {
      return renderPollListLoading();
    }

    if (!hasCompletedPolls) {
      return (
        <SampleDataOverlay message="Preview of completed polls — polls move here once all participants respond or the late window closes.">
          <SampleCompletedPollCards />
        </SampleDataOverlay>
      );
    }

    return (
      <div className="flex flex-col gap-5">
        <div>
          {historicalCompletedPollGroupsByPeriod.length > 0 ? (
            <h4 className="text-sm font-semibold text-gray-800 mb-3 mt-0">
              {formatQuarterPeriodLabel(period)}
            </h4>
          ) : null}
          {currentQuarterCompletedPolls.length > 0 ? (
            renderPollCycleCards(
              sortPollGroupsByLaunchDate(currentQuarterCompletedPolls),
              "completed",
              {
                selectable: true,
                selectablePeriod: period,
              }
            )
          ) : (
            <EngagementEmptyPanel
              variant="hub-compact"
              icon={List}
              title={`No completed polls for ${formatQuarterPeriodLabel(period)}`}
              hint="Polls appear here when all participants respond or the late window closes."
            />
          )}
        </div>

        {historicalCompletedPollGroupsByPeriod.length > 0 ? (
          <div className="flex flex-col gap-3">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-0 mt-0">
              Previous quarters
            </h4>
            {historicalCompletedPollGroupsByPeriod.map(
              ({ period: groupPeriod, groups }) => (
                <DiscoveryHubPollSectionAccordion
                  key={groupPeriod}
                  title={`${formatQuarterPeriodLabel(groupPeriod)} (${groups.length})`}
                  count={groups.length}
                  showCountBadge={false}
                  collapsible
                  defaultExpanded={false}
                >
                  {renderPollCycleCards(groups, "completed", {
                    selectable: true,
                    selectablePeriod: period,
                  })}
                </DiscoveryHubPollSectionAccordion>
              )
            )}
          </div>
        ) : null}
      </div>
    );
  };

  const renderHubContent = () => {
    if (
      !hasActivePolls &&
      activeHub !== "cycles" &&
      activeHub !== "completed" &&
      activeHub !== "ongoing" &&
      activeHub !== "late-window" &&
      activeHub !== "executive-summary" &&
      activeHub !== "draft-leaderboard" &&
      activeHub !== "approval-pending"
    ) {
      if (cycleExistsInList) {
        return (
          <EngagementEmptyPanel
            variant="hub-page"
            icon={List}
            title={`Cycle ${currentQuarterPeriod} exists but could not be loaded.`}
            hint="Check the Cycle history tab or refresh. If launch failed earlier, try Launch Poll again to finish setup."
            action={
              <Button variant="secondary" onClick={() => load()}>
                Refresh
              </Button>
            }
          />
        );
      }
      return (
        <EngagementEmptyPanel
          variant="hub-page"
          icon={ClipboardCheck}
          title="No active poll cycles"
          hint="Launch a poll cycle to begin quarterly engagement collection."
        />
      );
    }

    switch (activeHub) {
      case "ongoing":
        return (
          <>
            {renderStatusChips()}
            <DiscoveryHubPollSectionAccordion
              title="Polls"
              count={ongoingPollGroups.length}
              pendingReminderCount={totalOngoingPendingReminders}
              onSendReminderToAll={() =>
                requestBulkReminders(
                  allOngoingPendingReminders,
                  allOngoingPoll2PendingReminders
                )
              }
              reminderLoading={bulkReminderLoading}
              reminderDisabled={!!reminderLoadingId || hubRefreshing}
              bodyClassName="min-h-[calc(100vh-470px)]"
            >
              {renderPollCycleList(
                "No ongoing polls",
                "Polls appear here while collection is open and before the due date.",
                "ongoing",
                {
                  showReminders: true,
                  loading: hubRefreshing,
                }
              )}
            </DiscoveryHubPollSectionAccordion>
          </>
        );
      case "late-window":
        return (
          <>
            {renderStatusChips()}
            <DiscoveryHubPollSectionAccordion
              title="Overdue Polls"
              count={lateWindowPollGroups.length}
              pendingReminderCount={totalLateWindowPendingReminders}
              onSendReminderToAll={() =>
                requestBulkReminders(
                  allLateWindowPendingReminders,
                  allLateWindowPoll2PendingReminders
                )
              }
              reminderLoading={bulkReminderLoading}
              reminderDisabled={!!reminderLoadingId || hubRefreshing}
              bodyClassName="min-h-[calc(100vh-470px)]"
            >
              {renderPollCycleList(
                "No late window polls",
                "Polls move here after the due date while the late window is still open.",
                "late-window",
                {
                  showReminders: true,
                }
              )}
            </DiscoveryHubPollSectionAccordion>
          </>
        );
      case "completed":
        return (
          <>
            {renderStatusChips()}
            <DiscoveryHubPollSectionAccordion
              title="Polls"
              count={currentQuarterCompletedPolls.length}
              bodyClassName="min-h-[calc(100vh-470px)]"
              headerAction={
                <Button
                  variant="outline-primary"
                  disabled={
                    selectedCompletedCycleIds.size === 0 ||
                    savingDraftLeaderboard
                  }
                  loading={savingDraftLeaderboard}
                  onClick={handleSaveDraftLeaderboard}
                  className="shrink-0"
                >
                  {savingDraftLeaderboard
                    ? "Saving…"
                    : `Save as Draft Leaderboard${
                        selectedCompletedCycleIds.size > 0
                          ? ` (${selectedCompletedCycleIds.size})`
                          : ""
                      }`}
                </Button>
              }
            >
              {renderCompletedPollsByPeriod({ loading: hubRefreshing })}
            </DiscoveryHubPollSectionAccordion>
          </>
        );
      case "cycles":
        return <CycleHistoryPanel rows={cycles} />;
      case "draft-leaderboard":
        return (
          <DraftLeaderboardPanel
            onSubmitted={load}
            onApproverChanged={refreshLeaderboardWorkflow}
            currentQuarterPeriod={currentQuarterPeriod}
            leaderboard={draftLeaderboard}
            onRefreshLeaderboard={() => loadLeaderboard(currentQuarterPeriod)}
            loading={loading || leaderboardLoading}
            isAssignedApproverView={isAssignedApproverView}
          />
        );
      case "approval-pending":
        return (
          <ApprovalPendingPanel
            onActionComplete={load}
            currentQuarterPeriod={currentQuarterPeriod}
            leaderboard={approvalPendingLeaderboard}
            selectedPeriod={approvalPendingPeriod || currentQuarterPeriod}
            onRefreshLeaderboard={() =>
              loadApprovalPendingLeaderboard(
                approvalPendingPeriod || currentQuarterPeriod
              )
            }
            loading={loading || leaderboardLoading || approvalPendingLoading}
          />
        );
      case "executive-summary":
        return (
          <ExecutiveSummaryPanel
            data={executiveSummary}
            loading={loading || executiveSummaryLoading}
          />
        );
      default:
        return null;
    }
  };

  const renderLoadingState = () => (
    <HubPageLoading message="Loading Discovery Hub…" />
  );

  return (
    <div className="w-full min-w-0 border border-[#dee0e4] rounded-[15px] bg-white p-6 min-h-[calc(100vh-260px)]">
      <div className="flex items-center justify-between mb-4">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2 flex-wrap">
            Discovery Hub
            {period ? (
              <span
                className="text-sm font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-full px-2.5 py-0.5"
                title={quarterDateRangeTooltip(period)}
              >
                {formatQuarterPeriodLabel(period)}
              </span>
            ) : null}
          </h3>
          <p className="text-sm text-gray-500 mb-0">
            Launch poll cycles, track sponsor responses, and manage quarterly
            engagement collection.
          </p>
        </div>
        <div className="ml-4 shrink-0">
          <Button
            variant="primary"
            onClick={openLaunchModal}
            loading={launchModalOpening}
            icon={<Plus size={16} />}
          >
            Launch Poll
          </Button>
        </div>
      </div>

      <div className="mb-6">
        <TabsComponent
          tabs={hubTabs}
          activeTab={activeHub}
          onTabChange={(id: string) =>
            isDiscoveryHubTab(id, hasApproverHubAccess) && setHub(id)
          }
          variant="tab-underline"
          className="border-b"
        />
      </div>

      {loading && !hubData ? renderLoadingState() : renderHubContent()}

      {launchOpen && (
        <LaunchPollModal
          key={launchModalKey}
          isOpen
          onClose={closeLaunchModal}
          form={launchForm}
          launchPeriod={periodForLaunch(poll1Cadence)}
          poll1AudienceOptions={poll1AudienceOptions}
          poll2AudienceOptions={poll2AudienceOptions}
          poll1CycleEndDate={launchContext?.poll1_cycle_end_date}
          poll2CycleEndDate={launchContext?.poll2_cycle_end_date}
          poll1CanLaunch={launchContext?.poll1_can_launch ?? false}
          poll2CanLaunch={launchContext?.poll2_can_launch ?? false}
          poll1BlockReason={launchContext?.poll1_block_reason}
          poll2BlockReason={launchContext?.poll2_block_reason}
          onPollTypeToggle={handlePollTypeToggle}
          onChange={handleLaunchFormChange}
          onLaunch={handleLaunch}
          launching={launchSubmitting}
        />
      )}

      <Modal
        isOpen={extendDueDateModal.isOpen}
        toggle={() => {
          if (!extendingDueDate) {
            setExtendDueDateModal(prev => ({ ...prev, isOpen: false }));
          }
        }}
        title="Extend Poll Due Date"
        size="3xl"
      >
        <div>
          <div className="bg-gray-50 border border-gray-200 rounded-md p-3 mb-6">
            <p className="text-base text-gray-900 mb-1">
              {extendDueDateModal.pollName}
            </p>
            <p className="text-sm text-gray-600 mb-0">
              Current Due Date: {extendDueDateModal.currentDueDate || "—"}
            </p>
          </div>
          <div className="mb-6">
            <DatePicker
              label="New Due Date"
              name="newDueDate"
              value={extendDueDateModal.newDueDate}
              onChange={e =>
                setExtendDueDateModal(prev => ({
                  ...prev,
                  newDueDate: e.target.value,
                }))
              }
              required
              min={new Date().toISOString().split("T")[0]}
            />
          </div>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason for extension
            </label>
            <textarea
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              rows={4}
              value={extendDueDateModal.reason}
              onChange={e =>
                setExtendDueDateModal(prev => ({
                  ...prev,
                  reason: e.target.value,
                }))
              }
              placeholder="Explain why the due date needs to be extended..."
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() =>
                setExtendDueDateModal(prev => ({ ...prev, isOpen: false }))
              }
              disabled={extendingDueDate}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={extendingDueDate}
              onClick={async () => {
                if (
                  !extendDueDateModal.newDueDate ||
                  !extendDueDateModal.reason.trim()
                ) {
                  toast.error(
                    "Please provide a new due date and reason for extension."
                  );
                  return;
                }
                setExtendingDueDate(true);
                try {
                  const result = await dispatch(
                    extendPollDueDate({
                      cycleId: extendDueDateModal.cycleId,
                      due_date: extendDueDateModal.newDueDate,
                      reason: extendDueDateModal.reason.trim(),
                    })
                  );
                  if (extendPollDueDate.rejected.match(result)) {
                    toast.error(
                      (result.payload as string) || "Failed to extend due date"
                    );
                    return;
                  }
                  toast.success("Poll due date extended successfully");
                  setExtendDueDateModal({
                    isOpen: false,
                    cycleId: "",
                    pollName: "",
                    currentDueDate: "",
                    newDueDate: "",
                    reason: "",
                  });
                  setHubRefreshing(true);
                  try {
                    await load();
                  } finally {
                    setHubRefreshing(false);
                  }
                } finally {
                  setExtendingDueDate(false);
                }
              }}
            >
              Extend Due Date
            </Button>
          </div>
        </div>
      </Modal>

      {pendingConfirm ? (
        <DiscoveryHubConfirmModal
          isOpen
          title={
            pendingConfirm.type === "save_draft"
              ? "Save as Draft Leaderboard"
              : pendingConfirm.type === "bulk_reminder"
                ? "Send Reminders to All"
                : "Send Reminder"
          }
          message={pendingConfirm.label}
          confirmLabel={
            pendingConfirm.type === "save_draft" ? "Save Draft" : "Send"
          }
          loading={confirmLoading}
          onConfirm={handleConfirmAction}
          onCancel={() => {
            if (!confirmLoading) setPendingConfirm(null);
          }}
        />
      ) : null}
    </div>
  );
};

export default DiscoveryHub;
